import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import {
  getCompareChannelShareRanking,
  getCompareHashtagMultiTimeline,
  getCompareTrendTimeline,
  getHashtagTableDate,
} from '@op/services';
import { useEffect, useMemo, useRef, useState } from 'react';
import { tableColumnsList } from '../constants';
import { DBO_ARR, DBO_REQ, DBO_V } from '@op/utils/format';
import { SortBy, sortable } from 'tea-component/lib/table/addons';
import { usePageInfo } from '@op/hooks/common';

const keys = tableColumnsList.map(item => item.value);

export function useTableData({
  filter,
  saveDefaultData,
}: {
  filter: any;
  saveDefaultData?: any;
}) {
  const { wrapperRequestTile, loading } = useCancelRequestHook({
    defaultLoading: true,
  });
  const [sort, setSort] = useState<SortBy>({ by: 'views', order: 'desc' });
  const [searchValue, setSearchValue] = useState('');
  const [data, setData] = useState([]);
  const gameInfoMap = useRef<{ [key: string]: any }>({});
  const [gameInfoList, setGameInfoList] = useState<Array<any>>([]);
  // const [total, setTotal] = useState(0);
  const { pageInfo, changePageInfo } = usePageInfo({
    defaultPageInfo: { page: 1, page_size: 10 },
  });

  function getTableData() {
    setData([]);
    // setTotal(0);
    wrapperRequestTile(getCompareChannelShareRanking, filter).then(
      (data: any) => {
        const {
          start_time,
          end_time,
          link_of_start_time,
          link_of_end_time,
          // total,
          list,
        } = data || {};
        const gameMap: any = {};
        const gameList: Array<any> = [];

        const tableData = (list || []).map((item: any) => {
          const { list, old_list, game } = item || {};
          const gameId = game.edition_unified_id;
          gameMap[gameId] = game;
          const data = dealwithTableData(list, old_list);

          gameList.push(game);
          return {
            ...game,
            ...data,
            game_id: game.edition_unified_id,
            times: {
              new: [start_time, end_time],
              old: [link_of_start_time, link_of_end_time],
            },
          };
        });
        gameInfoMap.current = gameMap;
        setGameInfoList(gameList);
        setData(tableData);
        // setTotal(total || 0);
      },
    );
  }
  useEffect(() => {
    if (!filter?.edition_unified_id) return;

    getTableData();
  }, [filter]);

  // 搜索 + 排序处理完成
  const { tableRecode, total, currentGame} = useMemo(() => {
    const table = data || [];
    table.sort(sortable.comparer([sort]));

    let currentGame = null;
    let list: Array<any> = [];

    table.forEach((item: any, index: number) => {
      const rank = index + 1 > 100 ? '>100' : index + 1;
      const info = { ...item, rank };
      if (item.game_id === filter.edition_unified_id) {
        currentGame = info;
      } else {
        list.push(info);
      }
    });

    // if (currentGame) {
    //   list = [currentGame, ...list];
    // }

    if (searchValue) {
      const realValue = searchValue.trim().toLocaleLowerCase();
      list = list.filter((item: any) => {
        const oriName = item?.entity_name?.toLocaleLowerCase();
        const cnName = item?.entity_name_cn?.toLocaleLowerCase();
        const enName = item?.entity_name_en?.toLocaleLowerCase();

        return (
          oriName?.includes?.(realValue) ||
          cnName?.includes?.(realValue) ||
          enName?.includes?.(realValue)
        );
      });
    }

    saveDefaultData?.([currentGame, ...list], true);
    return {
      tableRecode: list,
      total: table.length,
      currentGame,
    };
  }, [data, sort, searchValue]);

  return {
    tableRecode,
    currentGame,
    total,
    sort,
    setSort,
    pageInfo,
    changePageInfo,
    searchValue,
    setSearchValue,
    loading,
    gameInfoMap: gameInfoMap.current,
    gameInfoList,
  };
}

type TChartType = {
  game_id: string;
  seriesObj: { [key: string]: Array<any> };
  xAxis: Array<string>;
  isEmpty: boolean;
  color: string;
};

export function useTrendTimeline({
  filter,
  gameInfoMap,
  tab,
  selectedKeysMap,
}: {
  filter: any;
  gameInfoMap: any;
  tab: string;
  selectedKeysMap?: any;
}) {
  const [chart, setChart] = useState<Array<TChartType>>([]);
  const [tableRecode, setTableRecode] = useState<Array<any>>([]);
  const { wrapperRequestTile, loading } = useCancelRequestHook();

  function getTimelineData() {
    wrapperRequestTile(getCompareTrendTimeline, filter).then((data: any) => {
      const { timeline_list, timeline_of_end_time, timeline_of_start_time } =
        data || {};

      const charts: Array<any> = [];
      const sortMap: any = {};
      const table: Array<any> = [];

      (timeline_list || []).forEach((item: { game_id: any; list: any }) => {
        const { game_id, list } = item;

        const { color, sort = 0 } = selectedKeysMap[game_id] || {};
        const { tableRecord, ...data } = DBO_REQ.moreKeys({
          timeline: list,
          timeline_of_start_time,
          timeline_of_end_time,
          filter,
          needTable: true,
          moreLineTable: true,
        });
        sortMap[game_id] = sort;
        charts.push({ game_id, ...data, color });
        const gameInfo = gameInfoMap[game_id];

        table.push(
          ...(tableRecord || []).map(item => ({ ...item, ...gameInfo })),
        );
      });
      charts.sort(DBO_ARR.sortByMap(sortMap, item => item.game_id));
      setChart(charts);
      setTableRecode(table);
    });
  }

  useEffect(() => {
    if (!filter?.edition_unified_id) return;
    setChart([]);
    getTimelineData();
  }, [filter]);

  const data = useMemo(() => {
    const series: Array<any> = [];
    let xAxis: Array<string> = [];
    const legend: Array<string> = [];
    let isEmpty = true;

    (chart || []).forEach(item => {
      const { seriesObj, xAxis: localXAxis, game_id, color } = item;
      const info = seriesObj?.[tab] || [];

      if (info.length > 0 && isEmpty) {
        isEmpty = false;
      }

      series.push({
        name: game_id,
        data: info,
        type: 'line',
        itemStyle: { color: color },
      });

      if (xAxis.length === 0) {
        xAxis = localXAxis;
      }
      legend.push(game_id);
    });

    return { xAxis, series, legend, isEmpty };
  }, [chart, tab, selectedKeysMap]);

  return { data, loading, tableRecode };
}

export function dealwithTableData(newData: any, oldData: any) {
  return keys.reduce((pre, cur) => {
    const oldItem = oldData?.[cur] || 0;
    const newItem = newData?.[cur] || 0;
    const value = DBO_V.qoq(newItem, oldItem, 6);
    const key = `${cur}_qoq`;
    return { ...pre, [key]: value, [`old_${cur}`]: oldItem, [cur]: newItem };
  }, {});
}

function addMarketShareByView(views: number, total: number) {
  const value = DBO_V.percentageValue(views || 0, total || 0, 6);
  return {
    market_share_by_view: value,
  };
}

export function useHashtagTimeline({ filter }: { filter: any }) {
  const [data, setData] = useState<any[]>([]);
  // const [currentPage, setCurrentPage] = useState(1);
  const [extraDataLoading, setExtraDataLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const { wrapperRequestTile, loading } = useCancelRequestHook();

  const getTimelineData = async (hashtagList: string[], currentPage = 1) =>
    wrapperRequestTile(getCompareHashtagMultiTimeline, {
      ...filter,
      page: currentPage,
      selected_hash_tags: hashtagList,
    });

  const getHashtagData = async (currentPage = 1) =>
    wrapperRequestTile(getHashtagTableDate, {
      ...filter,
      page: currentPage,
    });

  useEffect(() => {
    if (!filter?.edition_unified_id) return;
    const fetchData = async () => {
      setData([]);
      try {
        const hashtagData: any = await getHashtagData();
        const {
          new_list,
          old_list,
          total,
          start_time,
          end_time,
          link_of_start_time,
          link_of_end_time,
        } = hashtagData;
        setTotal(total);
        const hashtagList = new_list.map((val: { name: string }) => val.name);
        const timelineData: any = await getTimelineData(hashtagList);

        const {
          sorts,
          timelines,
          timeline_of_end_time,
          timeline_of_start_time,
        } = timelineData || {};
        const { publications_timeline, views_timeline } = timelines || {};

        const publicationsTimeline = DBO_REQ.moreKeys({
          timeline: publications_timeline,
          timeline_of_start_time,
          timeline_of_end_time,
          filter,
          fixedKeyList: sorts,
        });

        const viewsTimeline = DBO_REQ.moreKeys({
          timeline: views_timeline,
          timeline_of_start_time,
          timeline_of_end_time,
          filter,
          fixedKeyList: sorts,
        });

        const data = dealWithHashtagData({
          newData: new_list,
          oldData: old_list,
          publicationsSeries: publicationsTimeline.series || [],
          viewsSeries: viewsTimeline.series || [],
          oldTimeList: [link_of_start_time, link_of_end_time],
          newTimeList: [start_time, end_time],
          xAxis: publicationsTimeline.xAxis || [],
        });

        setData(data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [filter]);

  const fetchMoreData = async (currentPage: number) => {
    try {
      setExtraDataLoading(true);
      const hashtagData: any = await getHashtagData(currentPage);
      const {
        new_list,
        old_list,
        start_time,
        end_time,
        link_of_start_time,
        link_of_end_time,
      } = hashtagData;

      const hashtagList = new_list.map((val: { name: string }) => val.name);
      const timelineData: any = await getTimelineData(hashtagList, currentPage);

      const { sorts, timelines, timeline_of_end_time, timeline_of_start_time } =
        timelineData || {};
      const { publications_timeline, views_timeline } = timelines || {};

      const publicationsTimeline = DBO_REQ.moreKeys({
        timeline: publications_timeline,
        timeline_of_start_time,
        timeline_of_end_time,
        filter,
        fixedKeyList: sorts,
      });

      const viewsTimeline = DBO_REQ.moreKeys({
        timeline: views_timeline,
        timeline_of_start_time,
        timeline_of_end_time,
        filter,
        fixedKeyList: sorts,
      });

      const data = dealWithHashtagData({
        newData: new_list,
        oldData: old_list,
        publicationsSeries: publicationsTimeline.series || [],
        viewsSeries: viewsTimeline.series || [],
        oldTimeList: [link_of_start_time, link_of_end_time],
        newTimeList: [start_time, end_time],
        xAxis: publicationsTimeline.xAxis || [],
      });

      setData(prev => [...prev, ...data]);
      setExtraDataLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  return {
    data,
    loading,
    extraDataLoading,
    fetchMoreData,
    isMoreToFetch: total > data?.length,
  };
}

function dealWithHashtagData({
  newData,
  oldData,
  publicationsSeries,
  viewsSeries,
  oldTimeList,
  newTimeList,
  xAxis,
}: {
  newData: any;
  oldData: any;
  publicationsSeries: Array<{ name: string; data: number[] }>;
  viewsSeries: Array<{ name: string; data: number[] }>;
  oldTimeList: string[];
  newTimeList: string[];
  xAxis: string[];
}) {
  const oldMap = Object.fromEntries(oldData.map(item => [item.name, item]));
  const publicationsMap = publicationsSeries.reduce<Record<string, any>>(
    (acc, cur) => {
      acc[cur.name] = cur.data;
      return acc;
    },
    {},
  );

  const viewsMap = viewsSeries.reduce<Record<string, any>>((acc, cur) => {
    acc[cur.name] = cur.data;
    return acc;
  }, {});

  return newData.map((newItem: any, _index: number) => {
    const oldItem = oldMap?.[newItem.name] || {};
    const res: any = {
      oldTimeList,
      newTimeList,
      xAxis,
    };
    return Object.keys(newItem).reduce((result: any, key: string) => {
      const newVal = newItem[key];
      const oldVal = oldItem?.[key] || 0;
      result[key] = newVal;
      if (key.includes('name')) {
        if (key === 'name') {
          result[`publications_timeline`] = publicationsMap[newItem[key]] || [];
          result[`views_timeline`] = viewsMap[newItem[key]] || [];
        }
        return result;
      }

      result[`old_${key}`] = oldVal;
      result[`${key}_qoq`] = DBO_V.qoq(newVal, oldVal, 6);

      return result;
    }, res);
  });
}
